import { Box, Slider, Typography } from "@mui/material";
import { FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import { zoullooColors } from "../../../../../theme";
import { FreelancerOnboardingMyProfessionalInformationStepViewModel } from "../../../../../ViewModels/FreelancerOnboardingMyProfessionalInformationStepViewModel";

interface DailyRateSectionProps {
	formik: FormikProps<FreelancerOnboardingMyProfessionalInformationStepViewModel>;
	defaultMaxDailyRate?: number;
	defaultMinDailyRate?: number;
	sliderRef?: React.RefObject<HTMLSpanElement>;
}

const DailyRateSection: React.FC<DailyRateSectionProps> = ({
	formik,
	defaultMaxDailyRate = 2000,
	defaultMinDailyRate = 50,
	sliderRef
}) => {
	const { t } = useTranslation();

	// Extract values from formik
	const minDailyRate = formik.values.dailyRateMin;
	const maxDailyRate = formik.values.dailyRateMax;

	// Check if there are any validation errors to display
	const hasMinError = formik.touched.dailyRateMin && formik.errors.dailyRateMin;
	const hasMaxError = formik.touched.dailyRateMax && formik.errors.dailyRateMax;
	const hasAnyError = hasMinError || hasMaxError;

	// Handle daily rate changes
	const handleDailyRateChange = (_: Event, newValue: number | number[]) => {
		const values = newValue as number[];

		formik.setFieldValue("dailyRateMin", values[0]);
		formik.setFieldValue("dailyRateMax", values[1]);
		setTimeout(() => {
			formik.setFieldTouched("dailyRateMin", true);
			formik.setFieldTouched("dailyRateMax", true);
		}, 10);
	};

	// Handle blur event
	const handleBlur = () => {
		formik.setFieldTouched("dailyRateMin", true);
		formik.setFieldTouched("dailyRateMax", true);
	};

	return (
		<Box sx={{ mb: 4 }}>
			<Typography variant="h4" sx={{ mb: 1 }}>
				{t("freelancerOnboardingMyProfessionalInformation.dailyRate.title")}
			</Typography>
			<Typography variant="body1" color="textSecondary" sx={{ mb: 2, whiteSpace: "pre-line" }}>
				{t("freelancerOnboardingMyProfessionalInformation.dailyRate.description")}
			</Typography>

			<Box>
				<Slider
					ref={sliderRef}
					value={[
						minDailyRate || defaultMinDailyRate,
						maxDailyRate || defaultMaxDailyRate
					]}
					onChange={handleDailyRateChange}
					onBlur={handleBlur}
					valueLabelDisplay="auto"
					min={defaultMinDailyRate}
					max={defaultMaxDailyRate}
					step={50}
					valueLabelFormat={value => `${value}€`}
					sx={{
						ml: 1,
						width: "70%",
						color: hasAnyError ? "error.main" : zoullooColors.secondary.main,
						"& .MuiSlider-thumb": {
							backgroundColor: hasAnyError ? "error.main" : zoullooColors.secondary.main
						},
						"& .MuiSlider-track": {
							color: hasAnyError ? "error.main" : zoullooColors.secondary.main
						},
						"& .MuiSlider-rail": {
							color: hasAnyError ? "error.light" : undefined
						}
					}}
				/>
				<Box
					sx={{
						width: "70%",
						display: "flex",
						justifyContent: "space-between"
					}}
				>
					<Typography variant="body2" color="textSecondary">
						{t("freelancerOnboardingMyProfessionalInformation.dailyRate.min")}:{" "}
						{minDailyRate || defaultMinDailyRate}€
					</Typography>
					<Typography variant="body2" color="textSecondary">
						{t("freelancerOnboardingMyProfessionalInformation.dailyRate.max")}:{" "}
						{maxDailyRate || defaultMaxDailyRate}€
					</Typography>
				</Box>

				{/* Display validation errors */}
				{hasAnyError && (
					<Box sx={{ mt: 1, width: "70%" }}>
						{hasMinError && (
							<Typography variant="body1" color="error" sx={{ display: "block" }}>
								{formik.errors.dailyRateMin}
							</Typography>
						)}
						{hasMaxError && (
							<Typography variant="body1" color="error" sx={{ display: "block" }}>
								{formik.errors.dailyRateMax}
							</Typography>
						)}
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default DailyRateSection;
